import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, ShieldCheck, MessageSquarePlus, Loader2, X as XIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve a /api/files/... path to absolute backend URL
const resolveUrl = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

// Lightbox overlay for clicking a thumbnail. Supports prev/next.
export const ReviewLightbox = ({ photos, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    if (index == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') onPrev?.();
      if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onClose, onPrev, onNext]);
  if (index == null || !photos?.[index]) return null;
  const photo = photos[index];
  const canPrev = photos.length > 1;
  const canNext = photos.length > 1;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="review-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        aria-label="Schließen"
        data-testid="lightbox-close"
      >
        <XIcon className="h-5 w-5" />
      </button>
      {canPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev?.(); }}
          className="absolute left-2 sm:left-6 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          aria-label="Vorheriges Foto"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      {canNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext?.(); }}
          className="absolute right-2 sm:right-6 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          aria-label="Nächstes Foto"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
      <img
        src={resolveUrl(photo.url)}
        alt="Kundenfoto"
        className="max-h-[88vh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/70">
        {index + 1} / {photos.length}
      </p>
    </div>
  );
};

const PhotoStrip = ({ photos, onOpen }) => {
  if (!photos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="review-photo-strip">
      {photos.map((p, i) => (
        <button
          key={p.id || i}
          type="button"
          onClick={() => onOpen?.(i)}
          className="h-20 w-20 rounded-xl overflow-hidden border border-neutral-200 hover:border-sky-400 hover:shadow-md transition-all"
          data-testid={`review-photo-thumb-${p.id || i}`}
        >
          <img src={resolveUrl(p.url)} alt="Kundenfoto" loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('de-DE', { dateStyle: 'medium' }); }
  catch { return ''; }
};

// Compact star row used in summary cards + list items
export const StarRow = ({ value = 0, size = 'h-4 w-4', interactive = false, onChange }) => {
  const v = Number(value) || 0;
  return (
    <div className="inline-flex items-center gap-0.5" role={interactive ? 'radiogroup' : 'img'} aria-label={`${v} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(v);
        const half = !filled && n - 0.5 <= v;
        const cls = filled || half ? 'text-amber-400 fill-amber-400' : 'text-neutral-300';
        return interactive ? (
          <button
            key={n}
            type="button"
            onClick={() => onChange?.(n)}
            aria-checked={n === Math.round(v)}
            role="radio"
            className="p-0.5 hover:scale-110 transition-transform"
            data-testid={`star-input-${n}`}
          >
            <Star className={`${size} ${cls}`} />
          </button>
        ) : (
          <Star key={n} className={`${size} ${cls}`} aria-hidden />
        );
      })}
    </div>
  );
};

// Inline rating chip for product cards on category overview
export const RatingChip = ({ summary }) => {
  if (!summary || !summary.count) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-bold text-amber-700"
      data-testid="rating-chip"
      title={`Durchschnitt ${summary.average} von ${summary.count} Bewertungen`}
    >
      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
      <span className="tabular-nums">{summary.average.toFixed(1)}</span>
      <span className="text-neutral-500 font-normal">({summary.count})</span>
    </span>
  );
};

// Hook: batch-fetch summaries for a list of product IDs (category page)
export const useProductRatingSummaries = (productIds) => {
  const [summaries, setSummaries] = useState({});
  const idsKey = useMemo(() => (productIds || []).filter(Boolean).sort().join(','), [productIds]);
  useEffect(() => {
    if (!idsKey) { setSummaries({}); return; }
    let stop = false;
    axios.get(`${API}/products-rating-summary-batch?ids=${encodeURIComponent(idsKey)}`)
      .then((r) => { if (!stop) setSummaries(r.data?.items || {}); })
      .catch(() => { /* silent */ });
    return () => { stop = true; };
  }, [idsKey]);
  return summaries;
};

// Bar in the summary distribution column (5 horizontal bars)
const DistributionBar = ({ star, count, total, onClick, active }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 text-left py-0.5 px-1 rounded-lg transition-colors ${active ? 'bg-amber-50' : 'hover:bg-neutral-50'}`}
      data-testid={`dist-bar-${star}`}
    >
      <span className="text-xs font-bold text-neutral-700 w-3 tabular-nums">{star}</span>
      <Star className="h-3 w-3 fill-amber-400 text-amber-400 shrink-0" />
      <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-neutral-500 w-7 text-right">{count}</span>
    </button>
  );
};

// Single review item
const ReviewItem = ({ review, onOpenLightbox }) => (
  <article className="border-t border-neutral-100 first:border-t-0 py-4" data-testid={`review-item-${review.id}`}>
    <header className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
      <div className="flex items-center gap-2">
        <StarRow value={review.rating} size="h-4 w-4" />
        <span className="font-bold text-sm text-neutral-900">{review.display_name}</span>
        {(review.verified_customer || review.verified_booking) && (
          <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
            <ShieldCheck className="h-3 w-3" /> Verifiziert
          </span>
        )}
      </div>
      <time className="text-xs text-neutral-500">{fmtDate(review.created_at)}</time>
    </header>
    {review.title && <h4 className="font-bold text-neutral-800 text-sm mb-1">{review.title}</h4>}
    <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{review.text}</p>
    <PhotoStrip
      photos={review.photos}
      onOpen={(idx) => onOpenLightbox?.(review.photos, idx)}
    />
  </article>
);

// Main section embedded in ProductDetail
const ProductReviews = ({ productId }) => {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(null); // 1..5 or null
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ photos: null, index: null });

  const openLightbox = (photos, idx) => setLightbox({ photos, index: idx });
  const closeLightbox = () => setLightbox({ photos: null, index: null });
  const prevPhoto = () => setLightbox((cur) => cur.photos ? ({ ...cur, index: (cur.index - 1 + cur.photos.length) % cur.photos.length }) : cur);
  const nextPhoto = () => setLightbox((cur) => cur.photos ? ({ ...cur, index: (cur.index + 1) % cur.photos.length }) : cur);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/products/${productId}/rating-summary`).then((r) => r.data),
        axios.get(`${API}/products/${productId}/reviews${filter ? `?star=${filter}` : ''}`).then((r) => r.data),
      ]);
      setSummary(s);
      setItems(l.items || []);
    } catch { /* no-op */ }
    finally { setLoading(false); }
  }, [productId, filter]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) {
    return (
      <section className="rounded-3xl border bg-white p-6 text-center text-neutral-500" data-testid="product-reviews-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Bewertungen werden geladen…
      </section>
    );
  }

  const hasReviews = summary && summary.count > 0;

  return (
    <section
      className="rounded-3xl border bg-white p-6"
      id="product-reviews"
      data-testid="product-reviews-section"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-xl font-extrabold text-neutral-900">Kundenbewertungen</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Alle Bewertungen stammen von Kunden mit verifizierter Buchung.</p>
        </div>
        <a
          href="/mein-konto?tab=bewerten"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-700 hover:text-sky-900 hover:underline"
          data-testid="open-review-form-link"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" /> Selbst bewerten
        </a>
      </div>

      {!hasReviews ? (
        <p className="text-sm text-neutral-500 text-center py-6">
          Noch keine Bewertungen. Du hast dieses Produkt gemietet? Logge Dich ein und teile Deine Erfahrung!
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
          <aside className="bg-neutral-50 rounded-2xl p-4 space-y-3">
            <div className="text-center">
              <p className="text-5xl font-extrabold text-amber-500 tabular-nums leading-none" data-testid="summary-average">
                {summary.average.toFixed(1)}
              </p>
              <div className="mt-1.5 flex justify-center"><StarRow value={summary.average} size="h-5 w-5" /></div>
              <p className="text-xs text-neutral-500 mt-1.5">
                Basierend auf <strong>{summary.count}</strong> {summary.count === 1 ? 'Bewertung' : 'Bewertungen'}
              </p>
            </div>
            <div className="space-y-1 pt-2 border-t">
              {[5, 4, 3, 2, 1].map((s) => (
                <DistributionBar
                  key={s}
                  star={s}
                  count={summary.distribution[s] || 0}
                  total={summary.count}
                  active={filter === s}
                  onClick={() => setFilter((cur) => (cur === s ? null : s))}
                />
              ))}
            </div>
            {filter && (
              <button
                type="button"
                onClick={() => setFilter(null)}
                className="text-xs text-sky-700 hover:underline font-bold w-full text-center"
                data-testid="clear-rating-filter"
              >
                Filter zurücksetzen
              </button>
            )}
          </aside>

          <div className="min-h-[8rem]" data-testid="reviews-list">
            {items.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-6">
                Keine {filter ? `${filter}-Sterne-` : ''}Bewertungen mit diesem Filter.
              </p>
            ) : (
              items.map((r) => <ReviewItem key={r.id} review={r} onOpenLightbox={openLightbox} />)
            )}
          </div>
        </div>
      )}
      <ReviewLightbox
        photos={lightbox.photos}
        index={lightbox.index}
        onClose={closeLightbox}
        onPrev={prevPhoto}
        onNext={nextPhoto}
      />
    </section>
  );
};

export default ProductReviews;
